<script setup lang="ts">
import { FileText, QrCode } from 'lucide-vue-next'
import QrcodeVue from 'qrcode.vue'
import CopyLink from '@/components/CopyLink.vue'
import PanelGroup from './PanelGroup.vue'
import { QR_EXPORT_SIZE, QR_COLOR, exportQrPng, qrFileName, qrLogoSettings } from '../lib/brandAppQr'

const QR_SIZE = 128

const { t } = useI18n()
const notification = useNotification()

const props = withDefaults(
  defineProps<{
    shareLink?: string
    logo?: string
    brandName?: string
  }>(),
  {
    shareLink: '',
    logo: undefined,
    brandName: undefined
  }
)

const logoSettings = computed(() => qrLogoSettings(props.logo, QR_SIZE))
const exportLogoSettings = computed(() => qrLogoSettings(props.logo, QR_EXPORT_SIZE))

const exportHost = ref<HTMLDivElement | null>(null)
const isExporting = ref(false)

const downloadQrCode = async () => {
  const canvas = exportHost.value?.querySelector('canvas')
  if (!canvas || isExporting.value) return
  isExporting.value = true
  try {
    const link = document.createElement('a')
    link.download = qrFileName(props.brandName)
    const { dataUrl, logoDrawn } = await exportQrPng(canvas, props.logo)
    link.href = dataUrl
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    if (!logoDrawn) notification.create('error', { text: t('pages.loyaltySupports.webapp.editor.share.qrLogoSkipped') })
  } finally {
    isExporting.value = false
  }
}

const generateFlyer = () => {
  const token = localStorage.getItem('jwt')

  window.open(`https://visuals.heypongo.com/?source=addons&type=plv&token=${token}`, '_blank')
}
</script>

<template>
  <PanelGroup :title="t('pages.loyaltySupports.webapp.editor.share.title')">
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-5">
        <div class="border-border relative shrink-0 rounded-2xl border bg-white p-3">
          <QrcodeVue
            class="block"
            :value="shareLink"
            :size="QR_SIZE"
            :foreground="QR_COLOR"
            :image-settings="logoSettings"
            level="H"
            render-as="svg"
          />
          <img
            v-if="logo"
            :src="logo"
            alt=""
            class="absolute top-1/2 left-1/2 size-7 -translate-x-1/2 -translate-y-1/2 rounded-lg object-cover"
          />
        </div>
        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <Button variant="outline" :disabled="isExporting" @click="downloadQrCode">
            <QrCode />
            {{ t('pages.loyaltySupports.webapp.editor.share.downloadQr') }}
          </Button>
          <Button variant="outline" @click="generateFlyer">
            <FileText />
            {{ t('pages.loyaltySupports.webapp.editor.share.flyer') }}
          </Button>
        </div>
      </div>
      <CopyLink id="shareLink" :link="shareLink" />
    </div>

    <div ref="exportHost" class="hidden" aria-hidden="true">
      <QrcodeVue
        :value="shareLink"
        :size="QR_EXPORT_SIZE"
        :foreground="QR_COLOR"
        :image-settings="exportLogoSettings"
        :margin="2"
        level="H"
        render-as="canvas"
      />
    </div>
  </PanelGroup>
</template>
